import { Apollo } from 'apollo-angular'
import { EventEmitter, Injectable } from '@angular/core'
import { ClrLoadingState } from '@clr/angular'

import { AuthService } from '../auth/auth.service'
import evaluateTestGql from '../home/studies-details/evaluateTest.gql'
import testStudyGql from '../home/studies-details/testStudy.gql'
import { evaluateTest } from '../home/studies-details/types/evaluateTest'
import {
  studiesDetails_study_by_pk_modality_phantom_test_routines,
  studiesDetails_study_by_pk_modality_phantom_test_routines_phantom_test_routine_questions_phantom_test_questions_phantom_test_question as question,
} from '../home/studies-details/types/studiesDetails'
import {
  testStudy,
  testStudyVariables,
} from '../home/studies-details/types/testStudy'
import { CommService } from './comm.service'
import { getAnalysisFailureMessage } from './errors/analysisFailure'
import { addErrorContext } from './errors/errorContext'
import {
  getUserFacingMessage,
  markUserFacingErrorShown,
} from './errors/userFacing'

export interface TestStudyState {
  routineSelected: number | undefined
  unevaluatedTestArrived: number | undefined
  answersSubmitted: boolean
  submitButton: ClrLoadingState
  questions: question[]
  answers: Answers
  /**
   * What the analyzer last told the technologist about this study: a
   * user-facing rejection (e.g. a phantom section missing from the scan) or a
   * failure of the analysis itself. Shown inline until the tech dismisses it
   * or starts another test.
   */
  lastError?: string
}

export interface PhantomTestingEvent {
  studyId: number
  testId: number
  type: 'image analysis complete' | 'test evaluation complete'
}

interface Answers {
  [questionId: number]: string | number
}

@Injectable({
  providedIn: 'root',
})
export class PhantomTestingService {
  events = new EventEmitter<PhantomTestingEvent>()

  state: {
    [studyId: number]: TestStudyState
  } = {}

  constructor(
    private auth: AuthService,
    private apollo: Apollo,
    private comm: CommService,
  ) {}

  /**
   * @param version routine version id (common/routineVersions.ts) to evaluate
   *   with; null = the routine's default evaluation
   */
  testStudy(studyId: number, routineId: number, version: string | null = null) {
    const state = this.state[studyId]
    state.routineSelected = routineId
    const user = this.auth.user
    if (!user) {
      return
    }
    const variables: testStudyVariables = {
      studyId,
      userId: user.id,
      routineId,
      version,
    }
    const mutation = testStudyGql
    this.apollo
      .mutate<testStudy>({
        mutation,
        variables,
      })
      .subscribe({
        next: (result) => {
          if (result.errors) {
            this.failTest(studyId, result.errors[0])
          }
          console.log('unevaluated test came in')
          state.unevaluatedTestArrived = result.data.testStudy.id
          const testId = state.unevaluatedTestArrived
          this.events.emit({
            studyId,
            testId,
            type: 'image analysis complete',
          })
          if (!this.onStudyDetailsPage()) {
            this.comm.alert(
              'success',
              `Test complete`,
              -1,
              `/home/phantomTests/${testId}`,
            )
          }
          if (state.answersSubmitted) {
            this.evaluateTest(studyId)
          }
        },
        error: (err) => this.failTest(studyId, err),
      })
  }

  /**
   * A test mutation failed. resetState() REPLACES this.state[studyId], so the
   * `state` const captured by the caller is stale afterwards; lastError is
   * written on the fresh object.
   *
   * Two kinds of failure carry a message written for the technologist:
   * user-facing errors (extensions.code USER_FACING: the analyzer could not
   * use the study and says what to do) and analyzer system failures
   * (ANALYSIS_SYSTEM_ERROR: the analysis crashed or timed out and says whether
   * trying again can help). Both are shown here, then rethrown *marked as
   * shown*: the global handler skips its own toast but still reports them to
   * the issue tracker (severity info and error) so we know when a customer
   * hits one. Anything else is rethrown as before: generic toast + Bugsnag
   * event.
   *
   * Either way the study and the routine are noted for the error first, so
   * the report says which test failed.
   */
  private failTest(studyId: number, err: unknown): never {
    const message = getUserFacingMessage(err) ?? getAnalysisFailureMessage(err)
    addErrorContext(err, {
      test: { studyId, routineId: this.state[studyId]?.routineSelected },
    })
    this.resetState(studyId)
    if (message !== undefined) {
      this.state[studyId].lastError = message
      if (!this.onStudyDetailsPage()) {
        // the study page re-creates its state on return, so a sticky toast
        // with a link back is the only carrier when the tech navigated away
        this.comm.alert('danger', message, -1, `/home/studies/${studyId}`)
      }
      markUserFacingErrorShown(err)
    }
    throw err
  }

  onStudyDetailsPage() {
    const studyDetailsRegex = /\/home\/studies\/\d+/gm
    return studyDetailsRegex.test(window.location.pathname)
  }

  allQuestionsAnswered(studyId: number) {
    return (
      Object.values(this.state[studyId].answers).filter(
        (v) => v !== undefined && v !== null,
      ).length === this.state[studyId].questions.length
    )
  }

  submitQuestions(studyId: number) {
    const state = this.state[studyId]
    if (this.allQuestionsAnswered(studyId)) {
      state.submitButton = ClrLoadingState.LOADING
      state.answersSubmitted = true
      if (state.unevaluatedTestArrived) {
        return this.evaluateTest(studyId)
      }
    }
    return undefined
  }

  evaluateTest(studyId: number) {
    const state = this.state[studyId]
    const phantomTestId = state.unevaluatedTestArrived
    const answers = JSON.stringify(state.answers)
    this.apollo
      .mutate<evaluateTest>({
        mutation: evaluateTestGql,
        variables: { phantomTestId, answers },
      })
      .subscribe({
        next: (result) => {
          if (result.errors) {
            this.failTest(studyId, result.errors[0])
          }
          state.submitButton = ClrLoadingState.SUCCESS
          this.events.emit({
            studyId,
            testId: result.data.evaluateTest.id,
            type: 'test evaluation complete',
          })
        },
        error: (err) => this.failTest(studyId, err),
      })
  }

  resetState(studyId: number, initialState?: TestStudyState) {
    this.state[studyId] = initialState || {
      routineSelected: undefined,
      unevaluatedTestArrived: undefined,
      answersSubmitted: false,
      submitButton: ClrLoadingState.DEFAULT,
      questions: undefined,
      answers: {},
      lastError: undefined,
    }
  }

  assembleQuestions(
    studyId: number,
    routineId: number,
    routines: studiesDetails_study_by_pk_modality_phantom_test_routines[],
  ) {
    this.state[studyId].questions = routines
      .find((routine) => routine.id === routineId)
      .phantom_test_routine_questions_phantom_test_questions.map(
        (manyToManyRel) => manyToManyRel.phantom_test_question,
      )
  }
}
